<script setup lang="ts">
  import { useQueryClient } from '@tanstack/vue-query';
  import { toTypedSchema } from '@vee-validate/zod';
  import * as changeCase from 'change-case';
  import { useForm } from 'vee-validate';
  import { computed, nextTick, onMounted, ref } from 'vue';
  import * as zod from 'zod';

  import type { Dispute } from '@stuut/api/models';
  import { DisputePriorityOptions } from '@stuut/api/models';
  import { FormControl, FormField, FormItem, FormLabel } from '@ui/components/form';
  import { MonetaryInput, MonetaryText } from '@ui/components/monetary';
  import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@ui/components/select';
  import { Separator } from '@ui/components/separator';
  import { toast } from '@ui/components/sonner';
  import { Textarea } from '@ui/components/textarea';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import DisputeReasonSelect from '@/components/disputes/DisputeReasonSelect.vue';
  import InvoiceSelect from '@/components/filters/InvoiceSelect.vue';
  import PartnerSelect from '@/components/partners/PartnerSelect.vue';
  import { AssigneeSelect } from '@/components/users';
  import { usePartners, useProfile, useUsers } from '@/store';
  import { useDisputeNavigation } from '@/utils/dispute';

  export interface DisputeInitialValues {
    amount: number;
    assignee: string;
    description: string;
    invoice: string;
    partner: string;
    priority: string;
    reason: string;
  }

  interface Props {
    disabled?: boolean;
    dispute?: Dispute;
    initialData: Record<string, any>;
    updateData?: Record<string, any>;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'complete'): void;
  }>();
  const userStore = useUsers();
  const users = computed(() => userStore.users);
  const profileStore = useProfile();
  const isAdmin = computed(() => profileStore.profile?.is_org_admin);
  const { gotoProposeDisputeTask: navigateToDisputeTask } = useDisputeNavigation();

  const selectedAssignee = computed(() =>
    users.value?.find((user) => user.id === formData.values.assignee),
  );

  const InvoiceSelectRef = ref<InstanceType<typeof InvoiceSelect>>();

  const queryClient = useQueryClient();
  const partnerStore = usePartners();

  const submitting = ref(false);
  const invoiceCacheUpt = ref(0);
  const partnerCacheUpt = ref(0);

  const isDisabled = computed(() => submitting.value || props.disabled);

  const disputeCurrency = computed(() => InvoiceSelectRef.value?.currency ?? 'USD');

  const getDefaultAssignee = () => {
    if (props.initialData?.assignee) return props.initialData.assignee;
    if (!profileStore.profile?.is_org_admin) return profileStore.profile?.id;
    return undefined;
  };

  const formData = useForm({
    initialValues: {
      amount: props.initialData?.amount,
      assignee: getDefaultAssignee(),
      description: props.initialData?.description,
      invoice: props.initialData?.invoice,
      partner: props.initialData?.partner,
      priority: props.initialData?.priority,
      reason: props.initialData?.reason,
    },
    validationSchema: toTypedSchema(
      zod.object({
        amount: zod.coerce.number().optional().nullable(),
        assignee: zod.string().optional().nullable(),
        description: zod.string().optional().nullable(),
        invoice: zod.string().min(1),
        partner: zod.string().min(1),
        priority: zod.string().optional().nullable(),
        reason: zod.string().min(1),
      }),
    ),
  });

  interface ValidateForm {
    invalidFields: string[];
    isInvalid: boolean;
    isValid: boolean;
    message: string;
    result: Record<string, any>;
  }

  const validateFormData = async (): Promise<ValidateForm> => {
    const result = await formData?.validate();

    const invalidFields = Object.keys(result?.results || {})
      .filter((key) => result?.results?.[key]?.valid === false)
      .map((field) => changeCase.sentenceCase(field));

    return {
      invalidFields,
      isInvalid: !result?.valid,
      isValid: result?.valid,
      message: `Fix these fields and retry: ${invalidFields.join(', ')}`,
      result,
    };
  };

  const getRequestParams = () => {
    const params: Record<string, any> = { ...formData?.values };

    params.items = [
      {
        amount: formData?.values?.amount,
        invoice: formData?.values?.invoice,
      },
    ];

    if (params.invoice) delete params.invoice;

    return {
      cleanParams: pruneEmptyValues(params),
      formData,
      params,
    };
  };

  const pruneEmptyValues = (obj: Record<string, any>) => {
    if (!obj) return;

    const isEmpty = (v) => v === '' || v === null || v === undefined;
    const allowedEmpty = [0, '0'];

    return Object.fromEntries(
      Object.entries(obj).filter(([, v]) => allowedEmpty.includes(v) || !isEmpty(v)),
    );
  };

  const initialUpdateData = computed(() =>
    props.updateData ? pruneEmptyValues(props.updateData) : undefined,
  );

  const updatePartner = (partner) => {
    const isNewPartner = formData.values?.partner !== partner?.id;

    if (partner?.id) formData.setFieldValue('partner', partner?.id);

    if (isNewPartner) {
      formData.setFieldValue('invoice', undefined);
      nextTick(() => (invoiceCacheUpt.value = invoiceCacheUpt.value + 1));
    }
  };

  const updateInvoice = async (invoice) => {
    if (invoice?.id) formData.setFieldValue('invoice', invoice?.id);

    if (!formData.values?.partner) {
      if (invoice?.partner_id) {
        await partnerStore?.getPeekFindSelect(invoice?.partner_id);
        formData.setFieldValue('partner', invoice?.partner_id);
      }

      nextTick(() => (partnerCacheUpt.value = partnerCacheUpt.value + 1));
    }
  };

  const submit = async (e: Event) => {
    e?.preventDefault();

    if (submitting.value) return;

    try {
      submitting.value = true;

      const { isInvalid, message } = await validateFormData();

      if (isInvalid) return toast.error(message);

      const { params } = getRequestParams();

      await stuut.disputes.create(params);

      toast.success('Dispute created');

      queryClient.invalidateQueries({
        exact: false,
        queryKey: ['disputes'],
      });

      emit('complete');
      emit('close');
    } catch (err: any) {
      toast.error(err.clientMessage);
    } finally {
      submitting.value = false;
    }
  };

  const gotoProposeDisputeTask = async (invoice) => {
    const disputeId = invoice?.dispute_items?.[0]?.dispute_id;
    if (disputeId) {
      emit('close');
      await navigateToDisputeTask(disputeId);
    }
  };

  onMounted(() => {
    if (initialUpdateData.value) {
      nextTick(() => {
        formData.setValues({
          ...initialUpdateData.value,
        });
      });
    }
  });

  defineExpose({
    formData,
    getRequestParams,
    submit,
    submitting,
    validateFormData,
  });
</script>

<template>
  <form class="space-y-5" @submit="submit">
    <div class="space-y-5">
      <div class="space-y-3">
        <FormField name="partner">
          <FormItem layout="horizontal" class="flex items-center space-y-0">
            <FormLabel class="w-40 font-normal text-xs flex items-center" :allow-error="false">
              Customer*
            </FormLabel>

            <PartnerSelect
              :key="partnerCacheUpt"
              class="flex-1"
              trigger-class="form-item-error form-item-dirty flex-1"
              trigger-size="sm"
              :disabled="Boolean(isDisabled || initialData?.partner)"
              :partner-id="formData.values?.partner"
              @update="updatePartner"
            />
          </FormItem>
        </FormField>

        <FormField name="invoice">
          <FormItem layout="horizontal" class="flex items-center space-y-0">
            <FormLabel class="w-40 font-normal text-xs" :allow-error="false"> Invoice* </FormLabel>

            <InvoiceSelect
              ref="InvoiceSelectRef"
              :key="invoiceCacheUpt"
              class="flex-1"
              trigger-size="sm"
              trigger-class="form-item-error form-item-dirty"
              query-name="dispute-form-invoice"
              :show-selected-amount="true"
              :status="['open']"
              :invoice-id="formData.values?.invoice"
              :partner-id="formData.values?.partner"
              :disabled="Boolean(isDisabled || initialData?.invoice)"
              :disabled-fn="(invoice) => Boolean(invoice.hasNonTerminalDispute)"
              @update="(invoice) => updateInvoice(invoice)"
            >
              <template
                #invoice_option="{
                  dispute_id,
                  has_non_terminal_dispute,
                  in_proposed_dispute,
                  invoice,
                }"
              >
                <template v-if="has_non_terminal_dispute">
                  <div class="flex items-center justify-end gap-x-2">
                    <strong class="text-destructive">
                      <template v-if="in_proposed_dispute"> DRAFT </template>

                      <template v-else> IN DISPUTE </template>
                    </strong>

                    <Tooltip v-if="dispute_id">
                      <TooltipTrigger as-child>
                        <RouterLink
                          :to="{
                            name: 'disputes.dispute',
                            params: { id: dispute_id },
                          }"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="pointer-events-auto"
                          @click.stop
                        >
                          <i class="fas fa-up-right-from-square text-link !text-2xs" />
                        </RouterLink>
                      </TooltipTrigger>

                      <TooltipContent class="z-[201]">
                        This invoice is already in dispute.

                        <RouterLink
                          :to="{
                            name: 'disputes.dispute',
                            params: { id: dispute_id },
                          }"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="pointer-events-auto text-link"
                          @click.stop
                        >
                          Click here
                        </RouterLink>

                        to view it.
                      </TooltipContent>
                    </Tooltip>

                    <Tooltip v-else-if="in_proposed_dispute">
                      <TooltipTrigger as-child>
                        <Button
                          class="pointer-events-auto"
                          variant="link"
                          size="inline"
                          @click.stop="gotoProposeDisputeTask(invoice)"
                        >
                          <i class="fas fa-up-right-from-square text-link !text-2xs" />
                        </Button>
                      </TooltipTrigger>

                      <TooltipContent class="z-[201]">
                        This invoice has a draft dispute awaiting activation.

                        <Button
                          class="pointer-events-auto text-link"
                          variant="link"
                          size="inline"
                          @click.stop="gotoProposeDisputeTask(invoice)"
                        >
                          Click here
                        </Button>

                        to view it.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </template>

                <MonetaryText
                  v-else
                  class="text-right"
                  :value="invoice?.total_amount"
                  :currency="invoice?.currency"
                  :compact="true"
                />
              </template>
            </InvoiceSelect>
          </FormItem>
        </FormField>

        <FormField name="reason">
          <FormItem layout="horizontal" class="flex flex-row items-center space-y-0 flex-1">
            <FormLabel class="w-40 font-normal text-xs" :allow-error="false"> Reason* </FormLabel>

            <DisputeReasonSelect
              :key="formData.values.reason"
              class="flex-1 w-full"
              trigger-class="form-item-error form-item-dirty"
              size="sm"
              :disabled="isDisabled"
              :reason-id="formData.values.reason"
              @click.stop.prevent
              @update="(reason) => formData.setFieldValue('reason', reason?.id)"
            />
          </FormItem>
        </FormField>
      </div>

      <Separator />

      <div class="space-y-3">
        <FormField name="amount">
          <FormItem layout="horizontal" class="flex items-center space-y-0">
            <FormLabel class="w-40 font-normal text-xs flex items-center" :allow-error="false">
              Disputed Amount
            </FormLabel>

            <div class="flex-1">
              <MonetaryInput
                :model-value="formData.values.amount ?? undefined"
                :currency="disputeCurrency"
                :disabled="isDisabled"
                hide-currency-select
                smallest-unit
                class="flex-1 form-item-error form-item-dirty"
                input-class="!h-7.5 !text-left px-3.5 form-item-dirty"
                @update:model-value="
                  (v) => {
                    formData.setFieldValue('amount', Math.abs(v));
                  }
                "
              />
            </div>
          </FormItem>
        </FormField>

        <FormField name="priority">
          <FormItem layout="horizontal" class="flex items-center space-y-0">
            <FormLabel class="w-40 font-normal text-xs" :allow-error="false"> Priority </FormLabel>

            <Select
              class="flex-1"
              :model-value="formData.values?.priority ?? undefined"
              :disabled="isDisabled"
              placeholder="Select priority (optional)"
              @update:model-value="
                (priority) => formData.setFieldValue('priority', priority as string)
              "
            >
              <FormControl>
                <SelectTrigger
                  class="flex-1 mt-0 px-3.5 text-xs focus:outline-none font-mono form-item-error form-item-dirty"
                  size="sm"
                >
                  <SelectValue class="text-left" placeholder="Select priority (optional)" />
                </SelectTrigger>
              </FormControl>

              <SelectContent>
                <SelectItem
                  v-for="priority in DisputePriorityOptions"
                  :key="priority.value"
                  :value="priority.value"
                >
                  {{ priority.value }}
                </SelectItem>
              </SelectContent>
            </Select>
          </FormItem>
        </FormField>

        <FormField name="description">
          <FormItem layout="horizontal" class="flex items-center space-y-0">
            <FormLabel class="w-40 font-normal text-xs flex items-center" :allow-error="false">
              Description
            </FormLabel>

            <FormControl>
              <Textarea
                v-model="formData.values.description"
                class="flex-1 text-xs !mt-0 form-item-dirty"
                placeholder="Optional short description..."
                :disabled="isDisabled"
                @update:model-value="
                  (description) => formData.setFieldValue('description', description as string)
                "
              />
            </FormControl>
          </FormItem>
        </FormField>
      </div>
    </div>

    <div class="space-y-3">
      <FormField v-slot="{ componentField }" name="assignee">
        <FormItem layout="horizontal" class="flex items-center space-y-0">
          <FormLabel class="w-40 font-normal text-xs" :allow-error="false">
            Set Assignee
          </FormLabel>

          <FormControl>
            <AssigneeSelect
              v-bind="componentField"
              size="sm"
              :assignee="selectedAssignee || null"
              trigger-class="form-item-dirty"
              :show-avatar="true"
              :disabled="isDisabled || !isAdmin"
              @selected="
                (assignee) => {
                  formData.setFieldValue('assignee', assignee?.id || null);
                }
              "
            />
          </FormControl>
        </FormItem>
      </FormField>
    </div>
  </form>
</template>

<style scoped></style>
